"use client";

import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	useTransition
} from "react";
import { createPortal } from "react-dom";

import {
	Button,
	type DefaultImageProps,
	Modal,
	Notification
} from "@pamperedchef/design-system";

import {
	searchConsultantByLocationServer,
	searchConsultantByNameServer
} from "@/app/actions/consultant";
import {
	ConsultantDetails,
	type ConsultantItem,
	FindConsultant
} from "@/app/features/shared/components/ConsultantFinder";
import type { AssignedConsultant } from "@/app/hooks/useAddToList";
import { type SessionState, useSessionStore } from "@/app/store";
import { debounce } from "@/app/utilities/debounce";
import type { ConsultantItemReference } from "@/types";

import * as styles from "./ConnectConsultantModal.module.scss";

export interface ConnectConsultantModalProps {
	isOpen: boolean;
	onOpenChange: (next: boolean) => void;
	onSelect: (consultant: AssignedConsultant) => void;
	googleMapsApiKey?: string;
}

type SearchFilter = "name" | "location";

const NAME_FILTER: SearchFilter = "name";
const LOCATION_FILTER: SearchFilter = "location";
const PROFILE_IMAGE_HOST = "https://www.pamperedchef.com";

const FILTERS = [
	{ "aria-label": "By name", value: NAME_FILTER, children: "By Name" },
	{ "aria-label": "Near me", value: LOCATION_FILTER, children: "Near Me" }
];

const COPY = {
	title: "Find a Consultant",
	byName: {
		inputLabel: "Search by consultant name",
		placeholder: "Consultant name"
	},
	byLocation: {
		inputLabel: "Search your address",
		placeholder: "City, state or zip code"
	},
	legend: "Pick a consultant to connect them to your list.",
	select: "Select consultant",
	searchAgain: "Search again",
	cancel: "Cancel",
	locationUnsupported: "Location search is not available in this browser.",
	locationFailed: "We could not find your location. Try searching by name."
};

function toItems(
	entries: ConsultantItemReference[]
): ConsultantItem<DefaultImageProps>[] {
	return entries.map((entry) => ({
		consultantId: entry.consultantId,
		consultantPk: entry.consultantPk,
		consultantName: entry.consultantName,
		location: entry.location,
		activePWS: entry.activePWS
	}));
}

export function ConnectConsultantModal({
	isOpen,
	onOpenChange,
	onSelect,
	googleMapsApiKey
}: ConnectConsultantModalProps) {
	const sessionState = useSessionStore(
		(state) => state.sessionState
	) as SessionState;
	const [filter, setFilter] = useState<SearchFilter>(NAME_FILTER);
	const [value, setValue] = useState("");
	const [suggestions, setSuggestions] = useState<ConsultantItemReference[]>(
		[]
	);
	const [isLoading, setIsLoading] = useState(false);
	const [isPending, startTransition] = useTransition();
	const [selected, setSelected] = useState<ConsultantItemReference | null>(
		null
	);
	const [error, setError] = useState<string | null>(null);
	const resolvedCoordinatesRef = useRef<string | null>(null);
	const locationSearchIdRef = useRef(0);
	const [container, setContainer] = useState<HTMLElement | null>(null);

	useEffect(() => {
		const element = document.createElement("div");
		element.setAttribute("data-react-aria-top-layer", "true");
		document.body.appendChild(element);
		setContainer(element);
		return () => {
			element.remove();
		};
	}, []);

	const isNameFilter = filter === NAME_FILTER;

	const reset = useCallback(() => {
		setFilter(NAME_FILTER);
		setValue("");
		setSuggestions([]);
		setIsLoading(false);
		setSelected(null);
		setError(null);
		resolvedCoordinatesRef.current = null;
	}, []);

	const close = useCallback(() => {
		reset();
		onOpenChange(false);
	}, [reset, onOpenChange]);

	const debouncedSearchByName = useMemo(
		() =>
			debounce(async (query: string, session: SessionState) => {
				if (!query) {
					setSuggestions([]);
					setIsLoading(false);
					return;
				}
				startTransition(async () => {
					try {
						const data = await searchConsultantByNameServer(
							query,
							session
						);
						setSuggestions(data);
					} catch {
						setSuggestions([]);
					} finally {
						setIsLoading(false);
					}
				});
			}, 300),
		[]
	);

	async function searchByCoordinates(coordinates: string): Promise<void> {
		const searchId = ++locationSearchIdRef.current;
		setIsLoading(true);
		try {
			const data = await searchConsultantByLocationServer(
				coordinates,
				sessionState
			);
			if (searchId !== locationSearchIdRef.current) return;
			setSuggestions(data);
		} catch {
			if (searchId === locationSearchIdRef.current) {
				setSuggestions([]);
			}
		} finally {
			if (searchId === locationSearchIdRef.current) {
				setIsLoading(false);
			}
		}
	}

	function handleInputChange(next: string, placeName?: string): void {
		setError(null);
		setValue(placeName ? placeName : next);

		if (!isNameFilter) {
			resolvedCoordinatesRef.current = placeName ? next : null;
			if (!placeName) return;
			void searchByCoordinates(next);
			return;
		}

		setIsLoading(true);
		debouncedSearchByName(next, sessionState);
	}

	async function handleLocationSearch(): Promise<void> {
		if (resolvedCoordinatesRef.current) {
			await searchByCoordinates(resolvedCoordinatesRef.current);
			return;
		}
		if (!navigator.geolocation) {
			setError(COPY.locationUnsupported);
			return;
		}
		setIsLoading(true);
		setError(null);
		navigator.geolocation.getCurrentPosition(
			async (position) => {
				const coordinates = `${position.coords.latitude},${position.coords.longitude}`;
				resolvedCoordinatesRef.current = coordinates;
				setValue("");
				await searchByCoordinates(coordinates);
			},
			() => {
				setError(COPY.locationFailed);
				setIsLoading(false);
			}
		);
	}

	function handleFilterChange(next: string): void {
		resolvedCoordinatesRef.current = null;
		setValue("");
		setSuggestions([]);
		setError(null);
		setFilter(next === LOCATION_FILTER ? LOCATION_FILTER : NAME_FILTER);
	}

	function handleSelection(consultantId: string): void {
		const match = suggestions.find(
			(entry) => entry.consultantId === consultantId
		);
		setSelected(match ?? null);
	}

	function confirm(): void {
		if (!selected) return;
		onSelect({
			consultantNumber: selected.consultantId,
			name: selected.consultantName,
			...(selected.location ? { location: selected.location } : {})
		});
		reset();
	}

	const copy = isNameFilter ? COPY.byName : COPY.byLocation;

	if (!container) {
		return null;
	}

	return createPortal(
		<Modal
			size="md"
			overlayClassName={styles["overlay"]}
			title={COPY.title}
			aria-label={COPY.title}
			isOpen={isOpen}
			isDismissable
			onClose={close}
			onClosePress={close}
			primaryButton={
				selected ? (
					<Button kind="primary" onPress={confirm}>
						{COPY.select}
					</Button>
				) : undefined
			}
			secondaryButton={
				selected ? (
					<Button kind="secondary" onPress={() => setSelected(null)}>
						{COPY.searchAgain}
					</Button>
				) : (
					<Button kind="secondary" onPress={close}>
						{COPY.cancel}
					</Button>
				)
			}
		>
			<div
				className={styles["body"]}
				data-component="ConnectConsultantModal"
			>
				{error ? (
					<Notification
						kind="error"
						content={error}
						cta={{ link: "", title: "" }}
					/>
				) : null}

				{selected ? (
					<ConsultantDetails
						consultantName={selected.consultantName}
						location={selected.location}
						avatarImageUrl={
							selected.imageUrl
								? `${PROFILE_IMAGE_HOST}${selected.imageUrl}`
								: undefined
						}
					/>
				) : (
					<FindConsultant
						headline=""
						inputLabel={copy.inputLabel}
						inputPlaceholder={copy.placeholder}
						legend={COPY.legend}
						filters={FILTERS}
						defaultFilter={NAME_FILTER}
						selectedFilter={filter}
						value={value}
						suggestions={{
							loading: isPending || isLoading,
							items: toItems(suggestions)
						}}
						onLocationSearch={
							isNameFilter ? undefined : handleLocationSearch
						}
						onSelection={handleSelection}
						onInputChange={handleInputChange}
						onFilterChange={handleFilterChange}
						googleMapsApiKey={googleMapsApiKey ?? ""}
					/>
				)}
			</div>
		</Modal>,
		container
	);
}
